import React, { useState } from 'react';
import { freeToolsAPI } from '../services/api';

const MatchLiteV2 = () => {
  const [formData, setFormData] = useState({
    jobDescription: '',
    resumeText: '',
    inputMethod: 'text', // 'text' or 'file'
    consentGiven: false
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.pdf') && !fileName.endsWith('.docx')) {
        setError('Please upload a PDF or DOCX file.');
        e.target.value = '';
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        e.target.value = '';
        return;
      }
      
      setResumeFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consentGiven) {
      setError('You must agree to the Privacy Policy to proceed.');
      return;
    }

    if (formData.jobDescription.length < 200) {
      setError('Job description must be at least 200 characters for accurate matching.');
      return;
    }

    // Validate resume input based on method
    if (formData.inputMethod === 'file') {
      if (!resumeFile) {
        setError('Please upload a resume file.');
        return;
      }
    } else {
      if (formData.resumeText.length < 50) {
        setError('Resume text must be at least 50 characters long.');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setRateLimited(false);
    setResults(null);

    try {
      const response = await freeToolsAPI.matchLiteV2(
        formData.jobDescription,
        formData.inputMethod === 'text' ? formData.resumeText : null,
        formData.inputMethod === 'file' ? resumeFile : null
      );
      
      setResults(response);
    } catch (err) {
      console.error('Match Lite V2 analysis failed:', err);
      
      if (err.response?.status === 429) {
        setRateLimited(true);
        setError('You have reached your daily limit of 5 free analyses. Try again tomorrow or upgrade for unlimited access.');
      } else if (err.response?.status === 413) {
        setError('File too large. Please upload a file smaller than 5MB.');
      } else if (err.response?.status === 400) {
        const errorData = err.response.data;
        setError(errorData.detail || 'Invalid input. Please check your data and try again.');
      } else {
        setError('Analysis failed. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpsellClick = async () => {
    try {
      await freeToolsAPI.trackAnalyticsEvent('match-lite_upsell_click', {
        source: 'match_lite_v2_results',
        score: results?.match_score
      });
    } catch (err) {
      console.error('Failed to track upsell click:', err);
    }
    
    // Navigate to pricing page
    window.location.href = '/pricing?from=match-lite-v2';
  };

  const LoadingSkeleton = () => (
    <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border animate-pulse">
      <div className="text-center">
        <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  const ResultsDisplay = ({ results }) => {
    const getScoreColor = (score) => {
      if (score >= 80) return 'from-green-500 to-emerald-600';
      if (score >= 60) return 'from-blue-500 to-indigo-600';
      if (score >= 40) return 'from-yellow-500 to-orange-600';
      return 'from-red-500 to-pink-600';
    };

    const getScoreDescription = (score) => {
      if (score >= 80) return "Excellent Match!";
      if (score >= 60) return "Good Match";
      if (score >= 40) return "Fair Match";
      return "Needs Improvement";
    };

    return (
      <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border">
        {/* Score Display */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br ${getScoreColor(results.match_score)} text-white text-3xl font-bold mb-4 shadow-lg`}>
            {results.match_score}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{getScoreDescription(results.match_score)}</h3>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">{results.summary_verdict}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Missing Skills */}
          {results.key_missing_skills && results.key_missing_skills.length > 0 && (
            <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-400">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Key Missing Skills
              </h4>
              <div className="space-y-2">
                {results.key_missing_skills.map((skill, index) => (
                  <div key={index} className="flex items-center">
                    <span className="w-2 h-2 bg-orange-400 rounded-full mr-3"></span>
                    <span className="text-gray-700">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upsell Hook */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-400">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Next Steps
            </h4>
            <p className="text-gray-700 mb-4">{results.upsell_hook}</p>
            <button
              onClick={handleUpsellClick}
              className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-md"
            >
              Unlock Full Analysis
              <svg className="inline-block ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Zero-Retention Notice */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-800 text-sm">
              <strong>Privacy Protected:</strong> Your resume file was processed in memory and never stored on our servers.
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Resume Match Lite
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              Get a free compatibility score with zero-retention privacy protection
            </p>
            <div className="flex justify-center space-x-4">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Free • No signup required
              </div>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Zero retention
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Rate Limit Banner */}
        {rateLimited && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-orange-800 font-medium">Daily limit reached</span>
            </div>
            <p className="text-orange-700 mt-1">
              You've used all 5 free analyses today. Upgrade for unlimited access or try again tomorrow.
            </p>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Job Description Input */}
            <div>
              <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700 mb-2">
                Job Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="jobDescription"
                name="jobDescription"
                rows={6}
                value={formData.jobDescription}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Paste the job description here... (minimum 200 characters)"
                required
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.jobDescription.length} characters
              </div>
            </div>

            {/* Resume Input Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Resume Input Method <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="inputMethod"
                    value="text"
                    checked={formData.inputMethod === 'text'}
                    onChange={handleInputChange}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Paste Text</div>
                    <div className="text-sm text-gray-500">Copy and paste your resume text</div>
                  </div>
                </label>
                
                <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="inputMethod"
                    value="file"
                    checked={formData.inputMethod === 'file'}
                    onChange={handleInputChange}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Upload File</div>
                    <div className="text-sm text-gray-500">PDF or DOCX (max 5MB)</div>
                  </div>
                </label>
              </div>

              {/* Resume Text Input */}
              {formData.inputMethod === 'text' && (
                <textarea
                  name="resumeText"
                  rows={8}
                  value={formData.resumeText}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Paste your resume text here... (minimum 50 characters)"
                  required
                />
              )}

              {/* Resume File Input */}
              {formData.inputMethod === 'file' && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    id="resume-file"
                  />
                  <label
                    htmlFor="resume-file"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Choose Resume File
                  </label>
                  <p className="mt-2 text-sm text-gray-500">
                    PDF or DOCX files up to 5MB
                  </p>
                  {resumeFile && (
                    <p className="mt-2 text-sm text-green-600 font-medium">
                      ✓ {resumeFile.name} ({Math.round(resumeFile.size / 1024)} KB)
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Consent Checkbox */}
            <div className="flex items-start">
              <input
                id="consentGiven"
                name="consentGiven"
                type="checkbox"
                checked={formData.consentGiven}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                required
              />
              <label htmlFor="consentGiven" className="ml-2 text-sm text-gray-700">
                I agree to the{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500 underline"
                >
                  Privacy Policy
                </a>{' '}
                <span className="text-red-500">*</span>
              </label>
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={loading || !formData.consentGiven}
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Analyzing...
                  </>
                ) : (
                  'Get Free Match Score'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Loading State */}
        {loading && <LoadingSkeleton />}

        {/* Results Display */}
        {results && !loading && <ResultsDisplay results={results} />}

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Free users get 5 analyses per day. Want unlimited access? <a href="/pricing" className="text-blue-600 hover:text-blue-500 underline">Upgrade now</a></p>
        </div>
      </div>
    </div>
  );
};

export default MatchLiteV2;